import React, { useState, useEffect, useRef } from 'react';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalFooter } from '../../../components/bootstrap/Modal';
import Button from '../../../components/bootstrap/Button';
import Badge from '../../../components/bootstrap/Badge';
import Icon from '../../../components/icon/Icon';
import Spinner from '../../../components/bootstrap/Spinner';
import { CashierScanRequest } from '../../../contexts/socketContext';
import { toast } from 'react-toastify';
import moment from 'moment';
import api, { backendUrl } from '../../constants/urls';

interface CashierScanNotificationProps {
	scanRequest: CashierScanRequest | null;
	onClose: () => void;
}

const CashierScanNotification: React.FC<CashierScanNotificationProps> = ({
	scanRequest,
	onClose,
}) => {
	const [processing, setProcessing] = useState(false);
	const [timeLeft, setTimeLeft] = useState<number>(0);

	console.log("Scan request", scanRequest);

	useEffect(() => {
		if (scanRequest) {
			playNotificationSound();
			const expireTime = moment(scanRequest.expireAt);
			const now = moment();
			const secondsLeft = expireTime.diff(now, 'seconds');
			setTimeLeft(secondsLeft > 0 ? secondsLeft : 0);
		}
	}, [scanRequest]);

	useEffect(() => {
		if (timeLeft <= 0) return;

		const timer = setInterval(() => {
			setTimeLeft((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					toast.warning('Transaction request expired');
					onClose();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [timeLeft, onClose]);

	const playNotificationSound = () => {
		try {
			const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
			
			const oscillator = audioContext.createOscillator();
			const gainNode = audioContext.createGain();
			
			oscillator.connect(gainNode);
			gainNode.connect(audioContext.destination);
			
			oscillator.frequency.value = 800;
			oscillator.type = 'sine';
			
			gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
			gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
			
			oscillator.start(audioContext.currentTime);
			oscillator.stop(audioContext.currentTime + 0.5);
			
			setTimeout(() => {
				const oscillator2 = audioContext.createOscillator();
				const gainNode2 = audioContext.createGain();
				
				oscillator2.connect(gainNode2);
				gainNode2.connect(audioContext.destination);
				
				oscillator2.frequency.value = 1000;
				oscillator2.type = 'sine';
				
				gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
				gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
				
				oscillator2.start(audioContext.currentTime);
				oscillator2.stop(audioContext.currentTime + 0.5);
			}, 200);
		} catch (error) {
			console.error('Error playing notification sound:', error);
		}
	};

	const handleApprove = async () => {
		if (!scanRequest) return;

		try {
			setProcessing(true);
			const response = await api.post(
				`${backendUrl}/admin/customers/approve/customer/transaction/${scanRequest.cashierPointId}`,
				{ code: scanRequest.code }
			);

			if (response.data.success) {
				toast.success(response.data.message || 'Transaction approved successfully!', {
					position: 'top-right',
				});
				onClose();
			} else {
				toast.error(response.data.message || 'Failed to approve transaction');
			}
		} catch (error: any) {
			console.error('Error approving transaction:', error);
			toast.error(
				error?.response?.data?.message ||
				error?.message ||
				'Failed to approve transaction'
			);
		} finally {
			setProcessing(false);
		}
	};

	const handleReject = () => {
		toast.info('Transaction request rejected');
		onClose();
	};

	const formatTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};

	const getTypeColor = (type: string) => {
		return type === 'deposit' ? 'success' : 'warning';
	};

	const getTypeIcon = (type: string) => {
		return type === 'deposit' ? 'ArrowDownward' : 'ArrowUpward';
	};

	return (
		<>
			<Modal
				isOpen={!!scanRequest}
				setIsOpen={onClose}
				size='lg'
				titleId='cashier-scan-notification'
				isStaticBackdrop={true}
				isScrollable={false}
				isCentered={true}
				isAnimation={true}>
				<ModalHeader setIsOpen={onClose} className='bg-primary text-white'>
					<ModalTitle id='cashier-scan-notification'>
						<div className='d-flex align-items-center'>
							<div className='position-relative me-3'>
								<Icon
									icon='QrCodeScanner'
									size='2x'
									className='pulse-animation'
								/>
								<span className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger pulse-badge'>
									<Icon icon='Notifications' size='sm' />
								</span>
							</div>
							<div>
								<div className='fw-bold fs-5'>Customer Scan Detected</div>
								<small className='opacity-75'>Transaction awaiting approval</small>
							</div>
						</div>
					</ModalTitle>
				</ModalHeader>

				<ModalBody>
					<div className='row g-4'>
						<div className='col-12'>
							<div className='alert alert-info d-flex align-items-center mb-0'>
								<Icon icon='Timer' className='me-2' size='lg' />
								<div className='flex-grow-1'>
									<strong>Time Remaining:</strong>
									<span className='ms-2 fs-5 fw-bold text-danger'>
										{formatTime(timeLeft)}
									</span>
								</div>
								<div className='text-end'>
									<small className='text-muted'>
										Expires: {moment(scanRequest?.expireAt)?.format('HH:mm:ss')}
									</small>
								</div>
							</div>
						</div>

						<div className='col-12'>
							<div className='card border-0 shadow-sm bg-light'>
								<div className='card-body'>
									<div className='d-flex align-items-center mb-3'>
										<Icon icon='Person' className='text-primary me-2' size='lg' />
										<h5 className='mb-0'>Customer Information</h5>
									</div>
									<div className='row g-3'>
										<div className='col-md-6'>
											<label className='form-label text-muted small mb-1'>
												<Icon icon='AccountCircle' className='me-1' />
												Full Name
											</label>
											<div className='fw-bold fs-6'>{scanRequest?.customer?.fullname}</div>
										</div>
										<div className='col-md-6'>
											<label className='form-label text-muted small mb-1'>
												<Icon icon='Phone' className='me-1' />
												Mobile Number
											</label>
											<div className='fw-bold fs-6'>{scanRequest?.customer?.mobileNumber}</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className='col-12'>
							<div className='card border-0 shadow-sm'>
								<div className='card-body'>
									<div className='d-flex align-items-center mb-3'>
										<Icon icon='Receipt' className='text-success me-2' size='lg' />
										<h5 className='mb-0'>Transaction Details</h5>
									</div>
									<div className='row g-3'>
										<div className='col-md-4'>
											<label className='form-label text-muted small mb-1'>Type</label>
											<div>
												<Badge color={getTypeColor(scanRequest?.type)} className='px-3 py-2'>
													<Icon icon={getTypeIcon(scanRequest?.type)} className='me-1' />
													{scanRequest?.type?.toUpperCase()}
												</Badge>
											</div>
										</div>
										<div className='col-md-4'>
											<label className='form-label text-muted small mb-1'>Amount</label>
											<div className='fw-bold fs-4 text-primary'>
												GMD {scanRequest?.amount?.toLocaleString()}
											</div>
										</div>
										<div className='col-md-4'>
											<label className='form-label text-muted small mb-1'>Verification Code</label>
											<div className='font-monospace fw-bold fs-4 text-danger'>
												{scanRequest?.code}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className='col-12'>
							<div className='alert alert-warning d-flex align-items-start mb-0'>
								<Icon icon='Info' className='me-2 mt-1' />
								<div>
									<strong>Important:</strong> Please verify the customer's identity and the
									verification code before approving this transaction. This action cannot be undone.
								</div>
							</div>
						</div>
					</div>
				</ModalBody>

				<ModalFooter>
					<div className='d-flex w-100 gap-3'>
						<Button
							color='danger'
							size='lg'
							icon='Close'
							className='flex-fill'
							onClick={handleReject}
							isDisable={processing}>
							Reject
						</Button>
						<Button
							color='success'
							size='lg'
							icon={processing ? undefined : 'CheckCircle'}
							className='flex-fill'
							onClick={handleApprove}
							isDisable={processing}>
							{processing && <Spinner isSmall inButton isGrow />}
							{processing ? 'Approving...' : 'Approve Transaction'}
						</Button>
					</div>
				</ModalFooter>
			</Modal>

			<style>{`
				@keyframes pulse {
					0%, 100% {
						transform: scale(1);
						opacity: 1;
					}
					50% {
						transform: scale(1.1);
						opacity: 0.8;
					}
				}

				@keyframes pulse-badge {
					0%, 100% {
						transform: translate(-50%, -50%) scale(1);
					}
					50% {
						transform: translate(-50%, -50%) scale(1.2);
					}
				}

				.pulse-animation {
					animation: pulse 2s ease-in-out infinite;
				}

				.pulse-badge {
					animation: pulse-badge 1.5s ease-in-out infinite;
				}
			`}</style>
		</>
	);
};

export default CashierScanNotification;
